// src/components/common/FormModal/FormModal.jsx
import React from 'react';
import styles from './FormModal.module.css';

/**
 * Reusable Form Modal with consistent styling and behavior
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSubmit - Submit handler
 * @param {string} props.title - Modal title
 * @param {string} props.subtitle - Optional subtitle
 * @param {React.ReactNode} props.children - Form fields
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.error - Error message
 * @param {string} props.success - Success message
 * @param {string} props.submitText - Custom submit button text
 * @param {string} props.cancelText - Custom cancel button text
 */
const FormModal = ({
    isOpen,
    onClose,
    onSubmit,
    title,
    subtitle,
    children,
    isLoading = false,
    error = '',
    success = '',
    submitText,
    cancelText = 'Cancelar',
    maxWidth = '860px',
}) => {
    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && !isLoading) {
            onClose();
        }
    };

    return (
        <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
            <div className={styles.formContainer} style={{ maxWidth }}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2>{title}</h2>
                        {subtitle && <p className={styles.modalSubtitle}>{subtitle}</p>}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.modalCloseButton}
                        disabled={isLoading}
                    >
                        ×
                    </button>
                </div>

                {error && (
                    <div className={`${styles.alert} ${styles.error}`}>
                        <span className={styles.alertIcon}>⚠️</span>
                        {error}
                    </div>
                )}

                {success && (
                    <div className={`${styles.alert} ${styles.success}`}>
                        <span className={styles.alertIcon}>✓</span>
                        {success}
                    </div>
                )}

                <form onSubmit={onSubmit} className={styles.formContent}>
                    {children}
                    <div className={styles.formActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            className={styles.secondaryButton}
                            disabled={isLoading}
                        >
                            {cancelText}
                        </button>
                        <button
                            type="submit"
                            className={styles.primaryButton}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className={styles.buttonSpinner}></span>
                                    Salvando...
                                </>
                            ) : (
                                submitText || 'Salvar'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FormModal;